import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  InputAdornment,
  TablePagination,
  Collapse,
  Avatar,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  LockReset as LockResetIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import AppHeader from "./components/TopBar";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
} from "./services/userApi";

const ROLE_OPTIONS = [
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
  { value: "admin", label: "Admin" },
];

const formatRole = (role) => {
  const found = ROLE_OPTIONS.find((r) => r.value === role);
  return found ? found.label : role;
};

const emptyCreateForm = () => ({
  username: "",
  full_name: "",
  email: "",
  password: "",
  role: "viewer",
  active: true,
});

const emptyEditForm = () => ({
  full_name: "",
  email: "",
  role: "viewer",
  active: true,
});

function UserManagement({ onBackToHome }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [editForm, setEditForm] = useState(emptyEditForm());

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ role: "", status: "" });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsers({
        page: page + 1,
        pageSize: rowsPerPage,
        name: debouncedSearch || null,
      });
      setUsers(data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to load users",
        severity: "error",
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((user) => {
    const matchesUsername =
      !debouncedSearch ||
      user.username.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      user.full_name.toLowerCase().includes(debouncedSearch.toLowerCase());

    const matchesRole = !filters.role || user.role === filters.role;
    const matchesStatus =
      filters.status === "" ||
      (filters.status === "active" && user.active) ||
      (filters.status === "inactive" && !user.active);

    return matchesUsername && matchesRole && matchesStatus;
  });

  const hasNextPage = users.length === rowsPerPage;
  const estimatedCount = hasNextPage
    ? (page + 2) * rowsPerPage
    : page * rowsPerPage + users.length;

  const handleAddUser = () => {
    setEditMode(false);
    setEditingUserId(null);
    setCreateForm(emptyCreateForm());
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setEditMode(true);
    setEditingUserId(user.user_id);
    setEditForm({
      full_name: user.full_name,
      email: user.email,
      role: user.role,
      active: user.active,
    });
    setOpenDialog(true);
  };

  const handleOpenDelete = (user) => {
    setUserToDelete(user);
    setDeleteConfirmText("");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || deleteConfirmText !== userToDelete.username) return;
    setActionLoading(true);
    try {
      await deleteUser(userToDelete.user_id);
      setSnackbar({
        open: true,
        message: `User "${userToDelete.username}" deleted successfully`,
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      if (filteredUsers.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        fetchUsers();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete user",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenResetPassword = (user) => {
    setResetUserId(user.user_id);
    setResetPassword("");
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetUserId || resetPassword.length < 8) return;
    setActionLoading(true);
    try {
      await resetUserPassword(resetUserId, resetPassword);
      setSnackbar({
        open: true,
        message: "Password reset successfully",
        severity: "success",
      });
      setResetDialogOpen(false);
      setResetPassword("");
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to reset password",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveUser = async () => {
    setActionLoading(true);
    try {
      if (editMode) {
        await updateUser(editingUserId, editForm);
        setSnackbar({
          open: true,
          message: "User updated successfully",
          severity: "success",
        });
      } else {
        await createUser(createForm);
        setSnackbar({
          open: true,
          message: "User created successfully",
          severity: "success",
        });
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to save user",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    const newActive = !user.active;
    setActionLoading(true);
    try {
      const updated = await updateUser(user.user_id, { active: newActive });
      setSelectedUser(updated);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === updated.user_id ? updated : u)),
      );
      setSnackbar({
        open: true,
        message: user.active
          ? "User deactivated successfully"
          : "User activated successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update user status",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ role: "", status: "" });
    setPage(0);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "error";
      case "editor":
        return "warning";
      default:
        return "default";
    }
  };

  const isCreateValid =
    createForm.username.length >= 3 &&
    createForm.full_name.length >= 2 &&
    createForm.email &&
    createForm.password.length >= 8;

  const isEditValid =
    editForm.full_name.length >= 2 && editForm.email;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "#f5f5f5",
      }}
    >
      <AppHeader />

      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: 600, color: "#003376" }}
        >
          User Management
        </Typography>

        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <TextField
                placeholder="Search by username or name..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  borderColor: "#003376",
                  color: "#003376",
                  "&:hover": {
                    borderColor: "#002855",
                    bgcolor: "rgba(0, 51, 118, 0.04)",
                  },
                }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddUser}
                sx={{
                  bgcolor: "#4caf50",
                  "&:hover": { bgcolor: "#45a049" },
                }}
              >
                Add User
              </Button>
            </Box>

            <Collapse in={showFilters}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filters.role}
                    label="Role"
                    onChange={(e) => handleFilterChange("role", e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {ROLE_OPTIONS.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="text"
                  onClick={clearFilters}
                  sx={{ color: "#d32f2f" }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Collapse>
          </Box>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} sx={{ color: "#003376" }} />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.user_id}
                      sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}
                    >
                      <TableCell>{user.user_id}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#003376",
                              fontSize: 14,
                            }}
                          >
                            {user.full_name.charAt(0)}
                          </Avatar>
                          {user.full_name}
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatRole(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.active ? "Active" : "Inactive"}
                          color={user.active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUser(user);
                            setViewDialogOpen(true);
                          }}
                          sx={{ color: "#4caf50" }}
                          title="View details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          sx={{ color: "#1976d2" }}
                          title="Edit user"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenResetPassword(user)}
                          sx={{ color: "#003376" }}
                          title="Reset password"
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDelete(user)}
                          sx={{ color: "#d32f2f" }}
                          title="Delete user"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={estimatedCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 49]}
            labelRowsPerPage="Rows per page:"
            nextIconButtonProps={{ disabled: !hasNextPage }}
            sx={{
              borderTop: "1px solid #e0e0e0",
              ".MuiTablePagination-toolbar": {
                justifyContent: "center",
              },
            }}
          />
        </Card>
      </Box>

      {/* Create / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !actionLoading && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonAddIcon sx={{ color: "#003376" }} />
            {editMode ? "Edit User" : "Add New User"}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {!editMode && (
              <TextField
                label="Username"
                fullWidth
                value={createForm.username}
                onChange={(e) =>
                  setCreateForm({ ...createForm, username: e.target.value })
                }
                required
                helperText="3–30 characters, letters, numbers, underscore only"
              />
            )}
            <TextField
              label="Full Name"
              fullWidth
              value={editMode ? editForm.full_name : createForm.full_name}
              onChange={(e) =>
                editMode
                  ? setEditForm({ ...editForm, full_name: e.target.value })
                  : setCreateForm({ ...createForm, full_name: e.target.value })
              }
              required
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editMode ? editForm.email : createForm.email}
              onChange={(e) =>
                editMode
                  ? setEditForm({ ...editForm, email: e.target.value })
                  : setCreateForm({ ...createForm, email: e.target.value })
              }
              required
            />
            {!editMode && (
              <TextField
                label="Password"
                type="password"
                fullWidth
                value={createForm.password}
                onChange={(e) =>
                  setCreateForm({ ...createForm, password: e.target.value })
                }
                required
                helperText="Min 8 chars with letter, number, and special (@$!%*?&)"
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editMode ? editForm.role : createForm.role}
                label="Role"
                onChange={(e) =>
                  editMode
                    ? setEditForm({ ...editForm, role: e.target.value })
                    : setCreateForm({ ...createForm, role: e.target.value })
                }
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    editMode ? editForm.active : createForm.active
                  }
                  onChange={(e) =>
                    editMode
                      ? setEditForm({ ...editForm, active: e.target.checked })
                      : setCreateForm({
                          ...createForm,
                          active: e.target.checked,
                        })
                  }
                  color="success"
                />
              }
              label={editMode ? "Active" : "Active on creation"}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={
              actionLoading ||
              (editMode ? !isEditValid : !isCreateValid)
            }
            sx={{
              bgcolor: "#4caf50",
              "&:hover": { bgcolor: "#45a049" },
            }}
          >
            {actionLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : editMode ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#003376" }}>
            User Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: 32,
                    bgcolor: "#003376",
                  }}
                >
                  {selectedUser.full_name.charAt(0)}
                </Avatar>
              </Box>
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>ID:</strong> {selectedUser.user_id}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Username:</strong> {selectedUser.username}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {selectedUser.full_name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Email:</strong> {selectedUser.email}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Role:</strong>{" "}
                  <Chip
                    label={formatRole(selectedUser.role)}
                    color={getRoleColor(selectedUser.role)}
                    size="small"
                  />
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={selectedUser.active ? "Active" : "Inactive"}
                    color={selectedUser.active ? "success" : "default"}
                    size="small"
                  />
                </Typography>
              </Box>
              <Divider />
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedUser.active}
                    onChange={() => handleToggleActive(selectedUser)}
                    disabled={
                      actionLoading ||
                      (selectedUser.role === "admin" && selectedUser.active)
                    }
                    color="success"
                  />
                }
                label={
                  selectedUser.active
                    ? "Deactivate account"
                    : "Activate account"
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            variant="contained"
            sx={{ bgcolor: "#003376", "&:hover": { bgcolor: "#002855" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Amazon-style Delete Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !actionLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#fef2f2", color: "#991b1b" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon />
            Delete User
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is permanent and cannot be undone. All sessions for this
            user will be invalidated.
          </Alert>
          {userToDelete && (
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                You are about to delete:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {userToDelete.full_name} ({userToDelete.username})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userToDelete.email}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            To confirm deletion, type the username{" "}
            <strong>{userToDelete?.username}</strong> below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type username to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={
              actionLoading ||
              deleteConfirmText !== userToDelete?.username
            }
            onClick={handleConfirmDelete}
          >
            {actionLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Delete permanently"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !actionLoading && setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "#003376" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockResetIcon />
            Reset Password
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for this user. Their current sessions will be
            invalidated.
          </Typography>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            helperText="Min 8 chars with letter, number, and special (@$!%*?&)"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={actionLoading || resetPassword.length < 8}
            sx={{ bgcolor: "#003376", "&:hover": { bgcolor: "#002855" } }}
          >
            {actionLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Reset Password"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserManagement;
